import { Button, ButtonGroup, Form, ListGroup, Modal } from 'react-bootstrap';
import { Dispatch, FormEvent, SetStateAction, useEffect, useReducer, useRef, useState } from 'react';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { FileWithUuid, formReducer, storageUploadTask } from '../../utils/utils';
import { useParams } from 'react-router-dom';
import { IconButton } from '../IconButton/IconButton';
import { faPaperclip, faSpellCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDetectChanges } from '../../../hooks/useDetectChanges';
import { v4 as uuidV4 } from 'uuid';
import { WithTooltip } from '../WithTooltip/WithTooltip';
import { useTranslation } from 'react-i18next';
import { useGaming } from '../../../contexts/GamingContext';
import styles from './TicketForm.module.scss';

const TicketFormComponent = ({
  className = 'mt-4',
  insideTicket = false,
  isAdmin = false,
  onSuccess = (id) => {},
  ticket = null,
  wide = false,
  submitLabel = null,
}) => {
  const { currentUser, userDataFmt, submitTicket, getTicketStatuses, storage } = useFirebase();
  const { track } = useGaming();
  const [formData, setFormData] = useReducer(formReducer, {});
  const { id } = useParams<{ id?: string }>();
  const formRef = useRef<HTMLFormElement>();
  const filesRef = useRef<HTMLInputElement>();
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState<any[]>(null);
  const [newStatus, setNewStatus] = useState<string>(null);
  const [predefinedRepliesModalOpen, setPredefinedRepliesModalOpen] = useState(false);
  const { t } = useTranslation();

  const detectChanges = useDetectChanges();

  const formControlMaxWidth = wide ? undefined : '500px';

  const acceptedFileTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif'];

  useEffect(() => {
    if (!isAdmin) return;
    getTicketStatuses().then((statuses) => {
      setStatuses(statuses.filter((status) => status.applicable || status.id == ticket.status.id));
    });
  }, [isAdmin]);

  useEffect(() => {
    if (!ticket) return;
    setFormData({ name: 'assign', value: ticket.assigneeUid == currentUser.uid || !ticket.assignee });
  }, [ticket]);

  const handleChange = (event) => {
    setFormData({
      name: event.target.name,
      value: event.target.value,
    });
  };
  const handleChangeCheck = (event) => {
    setFormData({
      name: event.target.name,
      value: event.target.checked,
    });
  };
  const handleChangeFile = (event) => {
    setFormData({
      name: event.target.name,
      value: [...(formData[event.target.name] || []), ...Array.from<File>(event.target.files).filter((file) => acceptedFileTypes.includes(file.type))],
    });
  };

  const handleUploadTask = (item: FileWithUuid, messageId: string, onProgress: (progress: number) => void) => {
    return storageUploadTask({
      item,
      storage,
      currentUser,
      path: `ticket_data/${id}/${messageId}/${item.uuid}`,
      onProgress,
    });
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const fullData: any = {
      assign: formData.assign,
      subject: formData.subject,
      message: formData.message,
      uid: currentUser?.uid ?? null,
      name: (() => {
        if (!userDataFmt) return null;
        if (isAdmin) return userDataFmt.profile.formData.nome + ' ' + userDataFmt.profile.formData.cognome[0] + '.';
        return userDataFmt.profile.formData.nome + ' ' + userDataFmt.profile.formData.cognome;
      })(),
      ticketId: id ?? null,
      isStaff: isAdmin,
    };

    if (isAdmin) {
      if (newStatus != ticket.status.code) fullData.newStatusCode = newStatus;
      if (!fullData.message && !fullData.newStatusCode) return;
    }

    setLoading(true);
    const { ticketId, messageId } = await submitTicket(fullData);

    if (formData.attachments?.length) {
      for (const attachment of formData.attachments) {
        attachment.uuid = uuidV4();
        attachment.progress = 0;
      }
      detectChanges();
      for (const attachment of formData.attachments) {
        await handleUploadTask(attachment, messageId, (progress) => {
          attachment.progress = progress;
          detectChanges();
        });
      }
    }

    setLoading(false);
    formRef.current?.reset();
    formData.attachments = [];
    onSuccess(ticketId);
    track('TICKET_SUBMITTED', { ticketId });
  };

  return (
    <>
      <Form className={`${insideTicket ? 'form-page form-page--xl ' : ''}${className} d-flex flex-column align-items-center position-relative`} onSubmit={onSubmit} ref={formRef}>
        {/*{loading && (
         <div className={'form-loader d-flex align-items-center justify-content-center'}>
          <div>Invio in corso&hellip;</div>
        </div>
      )}*/}
        {!insideTicket && (
           <div className={styles.fullWidth} style={{ maxWidth: formControlMaxWidth }}>
            <Form.Group>
              <Form.Control placeholder={t('labels.oggetto')} disabled={loading} name={'subject'} required onChange={handleChange} />
            </Form.Group>
          </div>
        )}
        {isAdmin && insideTicket && (
          <div className={'d-flex justify-content-end w-100 mb-2'}>
            <Button variant={'outline-light'} size={'sm'} onClick={() => setPredefinedRepliesModalOpen(true)}>
              <FontAwesomeIcon icon={faSpellCheck} className={'mr-1'} /> Risposte predefinite &hellip;
            </Button>
          </div>
        )}
         <div className={styles.fullWidth} style={{ maxWidth: insideTicket ? null : formControlMaxWidth }}>
          <Form.Group>
            <Form.Control
              as={'textarea'}
              disabled={loading}
              placeholder={insideTicket ? t('ticketForm.labels.aggiungiRisposta') : t('ticketForm.labels.dettagli')}
              name={'message'}
              rows={6}
              required={!isAdmin}
              value={formData.message}
              onChange={handleChange}
            />
          </Form.Group>
        </div>
        <div
           className={`d-flex flex-column flex-sm-row justify-content-between ${styles.fullWidth}`}
           style={{ maxWidth: insideTicket ? null : formControlMaxWidth }}
        >
          <div>
            {isAdmin && (
              <div className={'d-flex flex-column flex-sm-row align-items-start align-items-sm-center'}>
                <div className={'d-flex align-items-center w-100'}>
                  <span className={'mr-2'}>Cambia stato in:</span>
                  {!statuses && <code>{t('labels.caricamento')}&hellip;</code>}
                  {!!statuses && (
                    <select className={'ml-auto'} onChange={(e) => setNewStatus(e.target.value)} defaultValue={ticket.status.code} disabled={loading}>
                      {statuses.map((status) => (
                        <option key={status.id} value={status.code}>
                          {status.label} {status.id == ticket.status.id && '(non modificare)'}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <Form.Check
                  type={'checkbox'}
                  name={'assign'}
                  id={'assign'}
                  label={'Prendi in carico'}
                  disabled={ticket.assigneeUid == currentUser.uid || loading}
                  checked={formData.assign != null ? formData.assign : ticket.assigneeUid == currentUser.uid || !ticket.assignee}
                  onChange={handleChangeCheck}
                  className={`${styles.nowrap} ml-sm-4 ml-auto mt-2 mb-2`}
                />
              </div>
            )}
          </div>
          <div className={'d-flex align-items-center justify-content-end'}>
            <input type={'file'} accept={acceptedFileTypes.join(',')} name={'attachments'} multiple onChange={handleChangeFile} hidden ref={filesRef} />
            {insideTicket && <IconButton icon={faPaperclip} onClick={() => filesRef.current.click()} className={'mr-2'} disabled={loading} />}
            <Button variant={'primary'} className={insideTicket ? 'tootor-button tootor-button--secondary' : ''} type={'submit'} disabled={loading}>
              {submitLabel || t('labels.invia')}
            </Button>
          </div>
        </div>
        {!!formData.attachments?.length && (
          <ListGroup className={'w-100 mt-3'}>
             <ListGroup.Item className={`font-weight-bold ${styles.attachmentHeader}`}>
              {t('ticketForm.labels.fileAllegati')}
            </ListGroup.Item>
            {formData.attachments.map((attachment, i) => (
               <ListGroup.Item key={i.toString()} className={styles.attachmentItem}>
                <div className={'d-flex justify-content-between align-items-center'}>
                  <span>{attachment.name}</span>
                  {!loading && (
                    <Button
                      variant={'outline-danger'}
                      onClick={() => {
                        formData.attachments.splice(i, 1);
                        detectChanges();
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  )}
                  {loading && !attachment.progress && <small>{t('ticketForm.labels.preparazione')}&hellip;</small>}
                  {loading && !!attachment.progress && (
                    <small>
                      {t('ticketForm.labels.uploadInCorso')}&hellip; ({attachment.progress}%)
                    </small>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Form>

      {!!ticket && (
        <Modal size={'lg'} show={predefinedRepliesModalOpen} onHide={() => setPredefinedRepliesModalOpen(false)} scrollable>
          <Modal.Header closeButton>
            <Modal.Title>Risposte predefinite</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <ListGroup>
              {ticket.predefinedReplies.map((reply) => (
                 <ListGroup.Item key={reply.id} className={styles.predefinedReply}>
                  <div>
                    <strong>{reply.question}</strong>
                  </div>
                  <p className={'mb-0'}>{reply.answer}</p>
                  <div className={'d-flex justify-content-end'}>
                    <ButtonGroup size={'sm'}>
                      <Button
                        className="tootor-button tootor-button--secondary"
                        onClick={() => {
                          setFormData({
                            name: 'message',
                            value: reply.answer,
                          });
                          setPredefinedRepliesModalOpen(false);
                        }}
                      >
                        Risposte predefinite
                      </Button>
                      <Button
                        className="tootor-button tootor-button--secondary"
                        onClick={() => {
                          setFormData({
                            name: 'message',
                            value: formData.message + '\n' + reply.answer,
                          });
                          setPredefinedRepliesModalOpen(false);
                        }}
                      >
                        Aggiungi alla risposta corrente
                      </Button>
                    </ButtonGroup>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button className="tootor-button tootor-button--basic" onClick={() => setPredefinedRepliesModalOpen(false)}>
              Chiudi
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export { TicketFormComponent as TicketForm };
